import { cn } from '@cointracker/styleguide';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@cointracker/styleguide/src/LoggedIn/Table';
import { TableCell as TableCellContainer } from '@cointracker/styleguide/src/LoggedIn/Table/TableCell';

import {
  Body,
  Body4,
  Button,
  Heading,
} from '@cointracker/styleguide/src/LoggedIn';
import { Search } from '@cointracker/styleguide/src/LoggedIn/Inputs/Search';
import { CaretDownIcon, TrashIcon, UsersIcon } from '@phosphor-icons/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useState } from 'react';
import { NotesModal } from '../NotesModal';
import { TaxPayerCase, useTaxCaseStore } from '../context';

const EmptyState = ({ mode }: { mode: 'active' | 'closed' }) => {
  return (
    <div className="flex min-h-[300px] w-full flex-col items-center justify-center py-32">
      <div className="bg-background-secondary mb-16 flex h-64 w-64 items-center justify-center rounded-full">
        <UsersIcon size={32} weight="bold" className="text-text-tertiary" />
      </div>
      <div className="mb-24 flex max-w-[400px] flex-col items-center gap-8">
        <Heading variant="h5" className="text-center text-text-secondary">
          {mode === 'active' ? 'No active cases' : 'No closed cases'}
        </Heading>
        <Body variant="body3" className="text-text-tertiary text-center">
          {mode === 'active'
            ? "You don't have any cases at the moment. Add your first case to get started."
            : "You haven't closed any cases yet. Completed and canceled cases will appear here."}
        </Body>
      </div>
    </div>
  );
};

const NoSearchResults = ({
  mode,
  searchQuery,
}: {
  mode: 'active' | 'closed';
  searchQuery: string;
}) => {
  return (
    <div className="flex min-h-[200px] w-full flex-col items-center justify-center py-24">
      <div className="mb-24 flex max-w-[400px] flex-col items-center gap-8">
        <Heading variant="h5" className="text-center text-text-secondary">
          No results found
        </Heading>
        <Body variant="body3" className="text-text-tertiary text-center">
          No {mode} cases match &quot;{searchQuery}&quot;. Try adjusting your
          search terms.
        </Body>
      </div>
    </div>
  );
};

export const TaxProClientsTable = ({
  mode,
  taxYear,
}: {
  mode: 'active' | 'closed';
  taxYear: number | null;
}) => {
  const { state, updateCaseStatus, updateCaseNotes, deleteCase } =
    useTaxCaseStore();
  const { cases } = state;
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [selectedEditingCase, setSelectedEditingCase] =
    useState<TaxPayerCase | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const loginAsClientAndRedirectHandler =
    (publicId: string, taxpayerName: string, redirectUrl?: string) =>
    async () => {
      try {
        const isQuebecDemo = window.location.pathname.includes('quebec-demo');
        const isSpainDemo = window.location.pathname.includes('spain-demo');
        const isGovDemo = window.location.pathname.includes('gov-demo');

        if (isQuebecDemo) {
          localStorage.setItem('quebec_demo_viewing_as', taxpayerName);
          localStorage.removeItem('spain_demo_viewing_as');
          localStorage.removeItem('gov_demo_viewing_as');
        } else if (isSpainDemo) {
          localStorage.setItem('spain_demo_viewing_as', taxpayerName);
          localStorage.removeItem('quebec_demo_viewing_as');
          localStorage.removeItem('gov_demo_viewing_as');
        } else if (isGovDemo) {
          localStorage.setItem('gov_demo_viewing_as', taxpayerName);
          localStorage.removeItem('quebec_demo_viewing_as');
          localStorage.removeItem('spain_demo_viewing_as');
        }
        const res = await fetch('/tax-pros/login-as', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ public_id: publicId }),
        });

        if (!redirectUrl) {
          const { redirect_url } = await res.json();
          redirectUrl = redirect_url;
        }
        window.location.href = redirectUrl;
        return false;
      } catch {
        return false;
      }
    };

  const filteredCases = cases
    .filter((item) => {
      const modeMatch =
        mode === 'active'
          ? item.status === 'In progress' || item.status === 'Not started'
          : item.status === 'Completed' || item.status === 'Canceled';

      if (!modeMatch) return false;

      if (taxYear !== null) {
        const hasSelectedTaxYear = item.taxYears.includes(taxYear);
        if (!hasSelectedTaxYear) return false;
      }

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const matchesName = item.caseName.toLowerCase().includes(query);
        const matchesStatus = item.status.toLowerCase().includes(query);
        const matchesNotes = item.notes?.toLowerCase().includes(query) || false;
        const matchesTaxYears = item.taxYears.some((year) =>
          year.toString().includes(query),
        );

        return matchesName || matchesStatus || matchesNotes || matchesTaxYears;
      }

      return true;
    })
    .sort((a, b) => {
      const statusPriority = {
        'In progress': 0,
        'Not started': 1,
        Completed: 2,
        Canceled: 3,
      };

      return statusPriority[a.status] - statusPriority[b.status];
    });

  // Check if there are any cases of this mode type at all (ignoring search)
  const allCasesForMode = cases.filter((item) => {
    const modeMatch =
      mode === 'active'
        ? item.status === 'In progress' || item.status === 'Not started'
        : item.status === 'Completed' || item.status === 'Canceled';

    if (!modeMatch) return false;

    if (taxYear !== null) {
      return item.taxYears.includes(taxYear);
    }

    return true;
  });

  // If there are no cases at all for this mode, show the empty state
  if (allCasesForMode.length === 0) {
    return (
      <div className="overflow-hidden rounded-16 border border-line-card bg-background-card shadow-md">
        <EmptyState mode={mode} />
      </div>
    );
  }

  return (
    <div className="space-y-16">
      {/* Search Input */}
      <div className="px-24 pt-20">
        <Search
          placeholder={`Search ${mode} cases...`}
          value={searchQuery}
          onChange={setSearchQuery}
          size="md"
          fluid
        />
      </div>

      <div className="overflow-hidden rounded-16 border border-line-card bg-background-card shadow-md">
        {/* Show no search results if we have cases but search filtered them all out */}
        {filteredCases.length === 0 && searchQuery.trim() ? (
          <NoSearchResults mode={mode} searchQuery={searchQuery} />
        ) : (
          <Table>
            <NotesModal
              caseName={selectedEditingCase?.caseName}
              notes={selectedEditingCase?.notes || ''}
              isOpen={isNotesModalOpen}
              onDismiss={() => setIsNotesModalOpen(false)}
              onSave={(notes) => {
                if (selectedEditingCase?.caseName) {
                  updateCaseNotes(selectedEditingCase.caseName, notes);
                }
                setIsNotesModalOpen(false);
              }}
            />
            <TableHeader>
              <TableRow className="border-b border-line-tertiary bg-background-default">
                <TableHead className="p-16 font-medium text-text-secondary">
                  <Body4>Case name</Body4>
                </TableHead>
                <TableHead className="p-16 font-medium text-text-secondary">
                  <Body4>Status</Body4>
                </TableHead>
                <TableHead className="p-16 font-medium text-text-secondary">
                  <Body4>Tax Year(s)</Body4>
                </TableHead>
                <TableHead className="p-16 font-medium text-text-secondary">
                  <Body4>Notes</Body4>
                </TableHead>
                <TableHead className="p-16 text-right font-medium text-text-secondary">
                  <Body4>Actions</Body4>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCases.map((item, index) => (
                <TableRow
                  key={item.caseName}
                  className="group border-b border-line-card transition-colors duration-150 last:border-0 hover:bg-background-card-highlight"
                >
                  <TableCell className="p-16">
                    <TableCellContainer as="div" className="font-medium">
                      {item.caseName}
                    </TableCellContainer>
                  </TableCell>
                  <TableCell className="p-16">
                    <TableCellContainer as="div">
                      <StatusDropdown
                        status={item.status}
                        onChange={(newStatus) => {
                          updateCaseStatus(item.caseName, newStatus);
                        }}
                      />
                    </TableCellContainer>
                  </TableCell>
                  <TableCell className="p-16">
                    <TableCellContainer as="div">
                      {item.taxYears.length > 0
                        ? item.taxYears.sort((a, b) => b - a).join(', ')
                        : 'No years selected'}
                    </TableCellContainer>
                  </TableCell>
                  <TableCell className="max-w-[200px] p-16">
                    <TableCellContainer
                      as="div"
                      className="hover:bg-background-secondary line-clamp-2 flex max-h-[52px] min-h-[52px] cursor-pointer justify-start overflow-hidden whitespace-pre-line rounded-8 p-2 transition-colors duration-150 hover:bg-opacity-50"
                      onClick={() => {
                        setSelectedEditingCase(item);
                        setIsNotesModalOpen(true);
                      }}
                    >
                      {item.notes ? (
                        <span className="text-text-tertiary self-start italic">
                          {item.notes}
                        </span>
                      ) : (
                        <span className="text-text-tertiary self-center italic">
                          No notes added
                        </span>
                      )}
                    </TableCellContainer>
                  </TableCell>
                  <TableCell>
                    <TableCellContainer
                      as="div"
                      className="flex justify-end gap-2 opacity-90 group-hover:opacity-100"
                    >
                      <Button
                        variant="highEmphasis"
                        size="small"
                        className="px-16 py-8 transition-transform duration-150 hover:translate-y-[-1px]"
                        onClick={loginAsClientAndRedirectHandler(
                          index === 0
                            ? 'f183d24e-a83a-477a-8ebc-ca5254ef474f'
                            : '73db2b27-d552-48b2-88ec-174780110529',
                          item.caseName,
                          '/wallets',
                        )}
                      >
                        View case
                      </Button>
                      <Button
                        transparent
                        className="text-text-tertiary pr-8 transition-colors duration-150 hover:text-text-negative"
                        onClick={() => deleteCase(item.caseName)}
                      >
                        <TrashIcon size={16} weight="bold" />
                      </Button>
                    </TableCellContainer>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

interface StatusDropdownProps {
  status: TaxPayerCase['status'];
  onChange: (status: TaxPayerCase['status']) => void;
}

const StatusDropdown = ({ status, onChange }: StatusDropdownProps) => {
  const statusOptions: TaxPayerCase['status'][] = [
    'Completed',
    'Not started',
    'In progress',
    'Canceled',
  ];

  let dotColor = '';
  let textColor = '';
  switch (status) {
    case 'Completed':
      dotColor = 'bg-text-positive';
      textColor = 'text-text-primary';
      break;
    case 'Not started':
      dotColor = 'bg-accent-bold-yellow';
      textColor = 'text-text-primary';
      break;
    case 'Canceled':
      dotColor = 'bg-text-negative';
      textColor = 'text-text-primary';
      break;
    case 'In progress':
      dotColor = 'bg-accent-bold-blue';
      textColor = 'text-text-primary';
      break;
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className="px-3 hover:bg-background-secondary flex cursor-pointer items-center gap-6 rounded-full py-1 outline-none transition-colors duration-150">
        <div
          className={cn(
            'h-12 w-12 rounded-full border-[4px]',
            dotColor.replace('bg-', 'border-'),
          )}
        />
        <span className={cn('font-medium', textColor)}>{status}</span>
        <CaretDownIcon size={12} weight="bold" />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-50 min-w-[180px] overflow-hidden rounded-8 border border-line-tertiary bg-background-default shadow-elevation-2"
          sideOffset={5}
        >
          {statusOptions.map((option) => {
            let optionTextColor = '';
            switch (option) {
              case 'Completed':
                optionTextColor = 'text-text-primary';
                break;
              case 'Not started':
                optionTextColor = 'text-text-primary';
                break;
              case 'Canceled':
                optionTextColor = 'text-text-primary';
                break;
              case 'In progress':
                optionTextColor = 'text-text-primary';
                break;
            }

            return (
              <DropdownMenu.Item
                key={option}
                onClick={() => onChange(option)}
                className={cn(
                  'flex cursor-pointer items-center gap-6 p-8 pl-16 pr-24 transition-colors',
                  'hover:bg-background-secondary',
                  status === option && 'bg-background-secondary',
                )}
              >
                <div
                  className={cn(
                    'h-12 w-12 rounded-full border-[4px]',
                    option === 'Completed' && 'border-text-positive',
                    option === 'Not started' && 'border-accent-bold-yellow',
                    option === 'Canceled' && 'border-text-negative',
                    option === 'In progress' && 'border-accent-bold-blue',
                  )}
                />
                <span className={cn('font-medium', optionTextColor)}>
                  {option}
                </span>
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
